import { UserRole } from 'src/graphql/generated/graphql.generated';
import { PermissionsConfiguration } from 'src/lib/amg-authorization/permissions.model';
import {
  OpsCommand,
  OpsFeature,
  OpsFeatures,
} from '../graphql/generated/operations-mutations.generated';
import {
  OrgCommand,
  OrgFeature,
  OrgFeatures,
} from '../graphql/generated/organization-mutations.generated';

export enum TcsCustomPermission {
  AccessPublicPages = 'AccessPublicPages',
  AccessInsights = 'AccessInsights',
  AccessDevKit = 'AccessDevKit',
  ManageOperations = 'ManageOperations',
  ManageOrg = 'ManageOrg',
  GrantAllCompanyAccess = 'GrantAllCompanyAccess',
  ViewCards = 'ViewCards',
  AccessRegionsWithoutCommCenterAssignment = 'AccessRegionsWithoutCommCenterAssignment',
  AccessCommCentersWithinScope = 'AccessScopedCommCenters',
  EditUnitName = 'EditUnitName',
  ViewFleetStatusDashboard = 'ViewFleetStatusDashboard',
  ViewFleetStatusAllCompanies = 'ViewFleetStatusAllCompanies',
  ViewFixedWingFleetStatus = 'ViewFixedWingFleetStatus',
  ViewGroundFleetStatus = 'ViewGroundFleetStatus',
  ViewRotorWingFleetStatus = 'ViewRotorWingFleetStatus',
  ViewCrewMemberPII = 'ViewCrewMemberPII',
  ViewPatientPII = 'ViewPatientPII',
  ViewFullCardDetails = 'ViewFullCardDetails',
  EditUnitAbbreviation = 'EditUnitAbbreviation',
  EditUnitRegion = 'EditUnitRegion',
  EditUnitBase = 'EditUnitBase',
  SetUnitBase = 'SetUnitBase',
  EditUnitAdministrationFlag = 'EditUnitAdministrationFlag',
  Impersonate = 'Impersonate',
  ViewPermissionMatrix = 'ViewPermissionMatrix',
  ViewDeletedCards = 'ViewDeletedCards',
  UpdateVehicleCapabilities = 'UpdateVehicleCapabilities',
  SetupTransferCenterRegion = 'SetupTransferCenterRegion',
  DeleteTransferCenterCall = 'DeleteTransferCenterCall',
  ChangeVehicleCompany = 'ChangeVehicleCompany',
  ManageVehicleOos = 'ManageVehicleOos',
  ResendImageTrend = 'ResendImageTrend',
  DisplaySensitiveDetails = 'DisplaySensitiveDetails',
  ViewNotificationsFromAllUsers = 'ViewNotificationsFromAllUsers',
  AccessControlZonesWithinScope = 'AccessOpsControlZone',
  AccessAsb = 'AccessAsb',
  ResendCardToDataWarehouse = 'ResendCardToDataWarehouse',
  AccessOosRum = 'AccessOosRum',
  ManageOosRum = 'ManageOosRum',
  ReadOnlyCommCentersWithinScope = 'ReadonlyAccessScopedCommCenters',
  ReadonlyOperations = 'ReadonlyOperations',
  AccessRtt = 'AccessRtt',
  LockCard = 'LockCard',
  ViewLockedCards = 'ViewLockedCards',
  AccessSupport = 'AccessSupport',
  ManageDeclineReasons = 'ManageDeclineReasons',
  ManageCancellationReasons = 'ManageCancellationReasons',
  ManageOosTickets = 'ManageOosTickets',
  ManageQarReasons = 'ManageQarReasons',
  ReviewOosTickets = 'ReviewOosTickets',
  ManagePaipReasons = 'ManagePaipReasons',
  AccessPaipManager = 'AccessPaipManager',
}

export type TcsPermission = TcsCustomPermission | OpsCommand | OrgCommand;
export const tcsPermissions: TcsPermission[] = [
  ...Object.values(OpsCommand),
  ...Object.values(OrgCommand),
  ...Object.values(TcsCustomPermission),
];

export type TcsPermissionSet =
  | OrgFeature
  | OpsFeature
  | 'CrewMemberCompliance'
  | 'RestrictedUnitFields'
  | 'FleetStatus'
  | 'OpsReadonly';

export function buildTcsPermissions(): PermissionsConfiguration<
  UserRole,
  TcsPermission,
  TcsPermissionSet
> {
  const baseConfig: PermissionsConfiguration<UserRole, TcsPermission, TcsPermissionSet> = {
    roles: Object.values(UserRole),
    permissions: tcsPermissions,
    permissionSets: {
      FleetStatus: [
        TcsCustomPermission.ViewFleetStatusDashboard,
        TcsCustomPermission.ViewFixedWingFleetStatus,
        TcsCustomPermission.ViewRotorWingFleetStatus,
        TcsCustomPermission.ViewGroundFleetStatus,
      ],
      CrewMemberCompliance: [
        OrgCommand.AddOutOfComplianceCrewMember,
        OrgCommand.UpdateOutOfComplianceCrewMember,
        OrgCommand.RemoveCrewMemberFromOutOfCompliance,
      ],
      RestrictedUnitFields: [
        TcsCustomPermission.EditUnitRegion,
        TcsCustomPermission.EditUnitAdministrationFlag,
      ],
      OpsReadonly: [
        TcsCustomPermission.ReadOnlyCommCentersWithinScope,
        TcsCustomPermission.AccessRegionsWithoutCommCenterAssignment,
        TcsCustomPermission.ViewCards,
        TcsCustomPermission.ViewCrewMemberPII,
        TcsCustomPermission.ViewPatientPII,
        TcsCustomPermission.ViewFullCardDetails,
        TcsCustomPermission.DisplaySensitiveDetails,
      ],
      ...OpsFeatures,
      ...OrgFeatures,
    },
    grants: {
      all: {
        permissions: [TcsCustomPermission.AccessPublicPages],
      },
      roles: {
        AsbDataManager: {
          permissionSets: [OrgFeature.Vehicles],
          permissions: [
            OrgCommand.AddVehicleMaintenance,
            OrgCommand.DeleteVehicleMaintenance,
            OrgCommand.UpdateVehicleMaintenance,
          ],
        },
        AsbViewer: {
          permissions: [TcsCustomPermission.AccessAsb],
        },
        AviationDataManager: {
          permissionSets: [OrgFeature.VehicleModels, OrgFeature.Vehicles, OrgFeature.FuelTanks],
          permissions: [
            TcsCustomPermission.ManageOrg,
            TcsCustomPermission.ChangeVehicleCompany,
            TcsCustomPermission.AccessInsights,
          ],
        },
        AviationSupervisor: {
          permissions: [
            TcsCustomPermission.ManageVehicleOos,
            TcsCustomPermission.ManageOperations,
            TcsCustomPermission.AccessInsights,
          ],
          inherits: [UserRole.Pilot],
        },
        Biller: {
          permissions: [
            TcsCustomPermission.ManageOperations,
            TcsCustomPermission.AccessInsights,
            TcsCustomPermission.ViewDeletedCards,
          ],
        },
        ImageTrend: {
          permissions: [
            TcsCustomPermission.ResendImageTrend,
            TcsCustomPermission.AccessInsights,
            TcsCustomPermission.ResendCardToDataWarehouse,
          ],
        },
        BillerCorrectionsTeam: {
          permissions: [OpsCommand.UpdateCardBillable],
          inherits: [UserRole.Biller],
        },
        Communications: {
          permissions: [
            ...OpsFeatures.Units.filter(
              (p) =>
                p !== OpsCommand.TeleportUnit &&
                p !== OpsCommand.DisableUnit &&
                p !== OpsCommand.RemoveVehicleFromUnit &&
                p !== OpsCommand.RemoveDelayFromUnit &&
                p !== OpsCommand.DelayUnit,
            ),
            TcsCustomPermission.ManageOperations,
            TcsCustomPermission.AccessInsights,
            TcsCustomPermission.AccessCommCentersWithinScope,
            TcsCustomPermission.ViewCards,
            OpsCommand.ReOpenCard,
            TcsCustomPermission.AccessRegionsWithoutCommCenterAssignment,
            TcsCustomPermission.ViewCrewMemberPII,
            TcsCustomPermission.ViewPatientPII,
            TcsCustomPermission.DisplaySensitiveDetails,
            TcsCustomPermission.ViewFullCardDetails,
            OpsCommand.AddMedicalControlContactLogEntry,
            TcsCustomPermission.AccessRtt,
          ],
          permissionSets: [
            OpsFeature.Timestamps,
            OpsFeature.QuestionnaireResponses,
            OpsFeature.ReceivingFacilityRequests,
            'FleetStatus',
            OpsFeature.ActiveAlerts,
            OpsFeature.PassdownLogs,
          ],
        },
        CommunicationsSupervisor: {
          permissionSets: [
            'RestrictedUnitFields',
            OpsFeature.Operations,
            OpsFeature.TransportOffers,
            OpsFeature.Alerts,
            OpsFeature.LocationHazards,
            OpsFeature.NotificationGroups,
            OrgFeature.Agencies,
            OrgFeature.AgencyLocations,
            OrgFeature.Airports,
            OrgFeature.Fbos,
            OrgFeature.Locations,
            OrgFeature.Physicians,
            OpsFeature.Cards,
            OpsFeature.Requests,
            OpsFeature.UnitActivationRules,
            OrgFeature.SpecialtyTeams,
            OrgFeature.SpecialtyTeamMembers,
          ],
          permissions: [
            OrgCommand.AddMedicalFacility,
            OrgCommand.DeleteMedicalFacility,
            OrgCommand.UpdateMedicalFacility,
            OrgCommand.UpdateBase,
            OpsCommand.TeleportUnit,
            OrgCommand.UpdateVehicle,
            TcsCustomPermission.ManageOrg,
            TcsCustomPermission.ViewDeletedCards,
            TcsCustomPermission.UpdateVehicleCapabilities,
            TcsCustomPermission.DeleteTransferCenterCall,
            OpsCommand.RemoveVehicleFromUnit,
            OrgCommand.UpdateVehicleStateInspection,
            TcsCustomPermission.ViewNotificationsFromAllUsers,
            OpsCommand.DelayUnit,
            OpsCommand.RemoveDelayFromUnit,
            TcsCustomPermission.AccessPaipManager,
          ],
          inherits: [UserRole.Communications, UserRole.ImageTrend],
        },
        FleetStatusOperations: {
          permissionSets: ['FleetStatus'],
          permissions: [
            TcsCustomPermission.ViewCrewMemberPII,
            TcsCustomPermission.ViewPatientPII,
            TcsCustomPermission.DisplaySensitiveDetails,
            TcsCustomPermission.AccessInsights,
          ],
        },
        FleetStatusViewer: {
          permissionSets: ['FleetStatus'],
          permissions: [
            TcsCustomPermission.ViewCrewMemberPII,
            TcsCustomPermission.DisplaySensitiveDetails,
          ],
        },
        Clinical: {
          permissionSets: ['FleetStatus'],
          permissions: [
            TcsCustomPermission.DisplaySensitiveDetails,
            TcsCustomPermission.AccessInsights,
          ],
        },
        External: {
          permissionSets: ['FleetStatus'],
        },
        QaSupervisor: {
          inherits: [UserRole.QaManager],
        },
        QaManager: {
          permissions: [
            ...OpsFeatures.Units.filter(
              (p) =>
                p !== OpsCommand.TeleportUnit &&
                p !== OpsCommand.DisableUnit &&
                p !== OpsCommand.RemoveVehicleFromUnit &&
                p !== OpsCommand.RemoveDelayFromUnit &&
                p !== OpsCommand.DelayUnit,
            ),
            TcsCustomPermission.ManageOperations,
            TcsCustomPermission.AccessInsights,
            TcsCustomPermission.ViewCards,
            OpsCommand.ReOpenCard,
            TcsCustomPermission.AccessRegionsWithoutCommCenterAssignment,
            TcsCustomPermission.ViewCrewMemberPII,
            TcsCustomPermission.ViewPatientPII,
            TcsCustomPermission.DisplaySensitiveDetails,
            TcsCustomPermission.ViewFullCardDetails,
            OpsCommand.AddMedicalControlContactLogEntry,
          ],
          permissionSets: [
            OpsFeature.Timestamps,
            OpsFeature.QuestionnaireResponses,
            OpsFeature.ReceivingFacilityRequests,
            'FleetStatus',
            OpsFeature.ActiveAlerts,
            OpsFeature.PassdownLogs,
          ],
        },
        ComplianceManager: {
          permissionSets: ['CrewMemberCompliance'],
          permissions: [TcsCustomPermission.ManageOrg, TcsCustomPermission.AccessInsights],
        },
        GlobalDataManager: {
          permissionSets: [
            OpsFeature.ConfigurationSettings,
            OpsFeature.TimestampTypes,
            OpsFeature.CancellationReasons,
            OpsFeature.DeclineReasons,
            OpsFeature.TransportReasons,
            OpsFeature.DeclineSecondaryReasons,
            OpsFeature.CancellationSecondaryReasons,
          ],
          inherits: [UserRole.OperationsDataManager],
          permissions: [
            TcsCustomPermission.ManageOrg,
            OrgCommand.AddCompany,
            TcsCustomPermission.ViewPermissionMatrix,
            TcsCustomPermission.AccessInsights,
          ],
        },
        GlobalPermissionsManager: {
          permissions: [TcsCustomPermission.ManageOrg, TcsCustomPermission.AccessInsights],
        },
        Developer: {
          permissions: 'all',
        },
        OperationsDataManager: {
          permissionSets: [
            OpsFeature.Alerts,
            OpsFeature.LocationHazards,
            OrgFeature.CommunicationCenters,
            OrgFeature.Programs,
            OrgFeature.Bases,
            OrgFeature.Vehicles,
            OrgFeature.VehicleModels,
            OrgFeature.Regions,
            OrgFeature.RegionGroups,
            OrgFeature.People,
            OrgFeature.FuelTanks,
            OpsFeature.NotificationTemplates,
            OpsFeature.OfferAlgorithms,
            OpsFeature.OutOfServiceReasons,
            OpsFeature.OutOfServiceSecondaryReasons,
            OpsFeature.Timelines,
            OpsFeature.Questions,
            OpsFeature.Questionnaires,
            OrgFeature.TransferCenters,
            OpsFeature.TransferReasons,
            OpsFeature.DiversionReasons,
          ],
          permissions: [
            ...OpsFeatures.Units.filter((p) => p !== OpsCommand.DisableUnit),
            TcsCustomPermission.ManageOrg,
            OrgCommand.UpdateCompany,
            TcsCustomPermission.UpdateVehicleCapabilities,
            TcsCustomPermission.SetupTransferCenterRegion,
            TcsCustomPermission.AccessInsights,
            TcsCustomPermission.SetUnitBase,
          ],
        },
        OperationsPermissionsManager: {
          permissions: [TcsCustomPermission.ManageOrg, TcsCustomPermission.AccessInsights],
        },
        OCC: {
          permissions: [
            TcsCustomPermission.AccessControlZonesWithinScope,
            TcsCustomPermission.AccessInsights,
          ],
        },
        OccReader: {
          permissions: [TcsCustomPermission.AccessControlZonesWithinScope],
        },
        Executive: {
          permissionSets: ['FleetStatus'],
          permissions: [
            TcsCustomPermission.ViewCrewMemberPII,
            TcsCustomPermission.ViewFleetStatusAllCompanies,
            TcsCustomPermission.DisplaySensitiveDetails,
            TcsCustomPermission.AccessInsights,
          ],
        },
        Stakeholder: {
          permissionSets: [OpsFeature.PaipReasons],
          permissions: [
            TcsCustomPermission.SetUnitBase,
            TcsCustomPermission.EditUnitBase,
            TcsCustomPermission.EditUnitName,
            TcsCustomPermission.EditUnitAbbreviation,
            TcsCustomPermission.LockCard,
            TcsCustomPermission.ViewLockedCards,
            TcsCustomPermission.AccessSupport,
            TcsCustomPermission.ManageCancellationReasons,
            TcsCustomPermission.ManageDeclineReasons,
            TcsCustomPermission.ManagePaipReasons,
          ],
        },
        FixedWingCoordinator: {
          permissions: [
            TcsCustomPermission.ViewFleetStatusDashboard,
            TcsCustomPermission.ViewFleetStatusAllCompanies,
            TcsCustomPermission.ViewFixedWingFleetStatus,
            TcsCustomPermission.AccessInsights,
          ],
        },
        Pilot: {
          permissionSets: ['FleetStatus'],
        },
        ProgramManager: {
          permissions: [TcsCustomPermission.AccessInsights],
        },
        Support: {
          permissionSets: [OrgFeature.Agencies, OrgFeature.AgencyLocations],
          permissions: [TcsCustomPermission.ManageOrg, TcsCustomPermission.AccessInsights],
        },
        AviationPermissionsManager: {
          permissions: [TcsCustomPermission.AccessInsights],
        },
        RemoteUser: {
          permissions: [TcsCustomPermission.AccessInsights],
        },
        TrmProgramAdmin: {
          permissions: [TcsCustomPermission.AccessInsights],
        },
        OpsReader: {
          permissions: [TcsCustomPermission.ReadonlyOperations],
          permissionSets: ['OpsReadonly'],
        },
        OosViewer: {
          permissions: [TcsCustomPermission.AccessOosRum],
        },
        OosMaintenanceController: {
          permissionSets: [
            OrgFeature.MaintenanceFacilities,
            OpsFeature.NotificationGroups,
            OpsFeature.NotificationGroupMembers,
            OpsFeature.NotificationTemplates,
          ],
          permissions: [
            TcsCustomPermission.AccessOosRum,
            TcsCustomPermission.ManageOosRum,
            TcsCustomPermission.ManageVehicleOos,
            TcsCustomPermission.ReviewOosTickets,
            TcsCustomPermission.ManageQarReasons,
            TcsCustomPermission.ManageOrg,
          ],
        },
        Mechanic: {
          permissions: [TcsCustomPermission.AccessOosRum, TcsCustomPermission.ManageOosTickets],
        },
        OosQA: {
          permissions: [TcsCustomPermission.ManageQarReasons],
        },
        OrgReader: {
          permissions: [TcsCustomPermission.ManageOrg],
        },
      },
    },
  };

  return baseConfig;
}
